import { useEffect, useMemo, useState } from 'react';
import {
  addDoc, arrayUnion, collection, deleteDoc, doc, getDoc, onSnapshot, query,
  serverTimestamp, setDoc, updateDoc, where, writeBatch,
  type DocumentData, type Unsubscribe, type UpdateData
} from 'firebase/firestore';
import { db } from './firebase';
import { todayISO } from './money';
import type {
  CurrencyCode, Expense, Group, Person, Settlement, SplitBasis, TripData, Unit
} from '../types';

/* ---------- ids ---------- */

export const newId = (): string =>
  (globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2) + Date.now().toString(36));

/** Unguessable, readable, no look-alike characters. */
export function newInviteCode(): string {
  const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
  const bytes = new Uint8Array(10);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
}

/* ---------- reads ---------- */

export function useMyGroups(uid: string | undefined) {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!uid) { setGroups([]); setLoading(false); return; }
    setLoading(true);
    const q = query(collection(db, 'groups'), where('memberUids', 'array-contains', uid));
    return onSnapshot(
      q,
      (snap) => {
        setGroups(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Group, 'id'>) })));
        setLoading(false);
        setError(null);
      },
      (e) => { setError(e.message); setLoading(false); }
    );
  }, [uid]);

  // Newest trips first; undated ones keep insertion order.
  const sorted = useMemo(
    () => groups.slice().sort((a, b) => (b.createdAtMs ?? 0) - (a.createdAtMs ?? 0) || a.name.localeCompare(b.name)),
    [groups]
  );
  return { groups: sorted, loading, error };
}

/** Group doc plus its three subcollections, live. */
export function useTrip(groupId: string | undefined) {
  const [group, setGroup] = useState<Group | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!groupId) { setLoading(false); return; }
    setLoading(true);
    const subs: Unsubscribe[] = [];
    const fail = (e: { message: string }) => { setError(e.message); setLoading(false); };

    subs.push(onSnapshot(doc(db, 'groups', groupId), (d) => {
      setGroup(d.exists() ? ({ id: d.id, ...(d.data() as Omit<Group, 'id'>) }) : null);
      setLoading(false);
    }, fail));

    subs.push(onSnapshot(collection(db, 'groups', groupId, 'units'), (s) => {
      setUnits(s.docs
        .map((d) => ({ id: d.id, ...(d.data() as Omit<Unit, 'id'>) }))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    }, fail));

    subs.push(onSnapshot(collection(db, 'groups', groupId, 'expenses'), (s) => {
      setExpenses(s.docs
        .map((d) => ({ id: d.id, ...(d.data() as Omit<Expense, 'id'>) }))
        .filter((e) => !e.deletedAt));
    }, fail));

    subs.push(onSnapshot(collection(db, 'groups', groupId, 'settlements'), (s) => {
      setSettlements(s.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Settlement, 'id'>) })));
    }, fail));

    return () => subs.forEach((u) => u());
  }, [groupId]);

  const data: TripData | null = useMemo(
    () => (group ? { group, units, expenses, settlements } : null),
    [group, units, expenses, settlements]
  );
  return { data, loading, error };
}

/* ---------- group lifecycle ---------- */

export interface NewHousehold { name: string; people: { name: string; weight: number; isChild?: boolean; age?: number | null }[] }

export async function createGroup(
  uid: string,
  input: { name: string; when: string; currency: CurrencyCode; defaultBasis: SplitBasis; households: NewHousehold[] }
): Promise<string> {
  const groupRef = doc(collection(db, 'groups'));
  const inviteCode = newInviteCode();

  // Two steps, deliberately. Rules evaluate every write in a batch against
  // COMMITTED state, and the units and invite rules get() the group to check
  // membership — so in one batch they see no group and fail with
  // permission-denied. Write the group, wait for the server to ack it, then
  // batch the rest.
  await setDoc(groupRef, {
    name: input.name,
    when: input.when,
    currency: input.currency,
    defaultBasis: input.defaultBasis,
    memberUids: [uid],
    inviteCode,
    archived: false,
    createdBy: uid,
    createdAt: serverTimestamp(),
    createdAtMs: Date.now()
  });

  const batch = writeBatch(db);
  input.households.forEach((h, i) => {
    const unitRef = doc(collection(db, 'groups', groupRef.id, 'units'));
    batch.set(unitRef, {
      name: h.name,
      color: (i % 6) + 1,
      order: i,
      // The creator is the first household by construction.
      linkedUids: i === 0 ? [uid] : [],
      people: h.people.map((p) => ({
        id: newId(), name: p.name, weight: p.weight,
        isChild: p.isChild ?? false, age: p.age ?? null
      }))
    });
  });

  batch.set(doc(db, 'invites', inviteCode), {
    groupId: groupRef.id, groupName: input.name, createdBy: uid, createdAt: serverTimestamp()
  });

  await batch.commit();
  return groupRef.id;
}

export interface InviteInfo { groupId: string; groupName: string }

export async function resolveInvite(code: string): Promise<InviteInfo | null> {
  const snap = await getDoc(doc(db, 'invites', code.trim().toUpperCase()));
  if (!snap.exists()) return null;
  const d = snap.data() as InviteInfo;
  return { groupId: d.groupId, groupName: d.groupName };
}

/**
 * Add yourself to a group. The security rules allow exactly this — one uid
 * added, nothing else touched — which is why invite links need no backend.
 */
export async function joinGroup(groupId: string, uid: string): Promise<void> {
  await updateDoc(doc(db, 'groups', groupId), { memberUids: arrayUnion(uid) });
}

export const setGroupField = (groupId: string, patch: Partial<Group>) =>
  updateDoc(doc(db, 'groups', groupId), patch as UpdateData<DocumentData>);

/* ---------- households ---------- */

export function addUnit(groupId: string, name: string, order: number, people: Person[]) {
  return addDoc(collection(db, 'groups', groupId, 'units'), {
    name, color: (order % 6) + 1, order, linkedUids: [], people
  });
}

export const updateUnit = (groupId: string, unitId: string, patch: Partial<Unit>) =>
  updateDoc(doc(db, 'groups', groupId, 'units', unitId), patch as UpdateData<DocumentData>);

export const deleteUnit = (groupId: string, unitId: string) =>
  deleteDoc(doc(db, 'groups', groupId, 'units', unitId));

/** Claim a household as yours. One household per user per trip. */
export async function linkUnit(groupId: string, units: Unit[], unitId: string, uid: string) {
  const batch = writeBatch(db);
  for (const u of units) {
    const has = u.linkedUids.includes(uid);
    if (u.id === unitId && !has) {
      batch.update(doc(db, 'groups', groupId, 'units', u.id), { linkedUids: arrayUnion(uid) });
    } else if (u.id !== unitId && has) {
      batch.update(doc(db, 'groups', groupId, 'units', u.id), {
        linkedUids: u.linkedUids.filter((x) => x !== uid)
      });
    }
  }
  await batch.commit();
}

export function setPersonWeight(groupId: string, unit: Unit, personId: string, weight: number) {
  return updateUnit(groupId, unit.id, {
    people: unit.people.map((p) => (p.id === personId ? { ...p, weight } : p))
  });
}

/* ---------- expenses ---------- */

export type ExpenseInput = Omit<Expense, 'id' | 'createdBy' | 'createdAt' | 'deletedAt'>;

export function addExpense(groupId: string, uid: string, input: ExpenseInput) {
  return addDoc(collection(db, 'groups', groupId, 'expenses'), {
    ...input, createdBy: uid, createdAt: serverTimestamp(), deletedAt: null
  });
}

export const updateExpense = (groupId: string, expenseId: string, patch: Partial<Expense>) =>
  updateDoc(doc(db, 'groups', groupId, 'expenses', expenseId),
    { ...patch, updatedAt: serverTimestamp() } as UpdateData<DocumentData>);

/**
 * Soft delete. A hard delete races badly with an offline device, which would
 * happily re-create the document it still has in its cache.
 */
export const deleteExpense = (groupId: string, expenseId: string) =>
  updateDoc(doc(db, 'groups', groupId, 'expenses', expenseId), { deletedAt: serverTimestamp() });

/* ---------- settlements ---------- */

export function addSettlement(
  groupId: string, uid: string,
  input: { fromUnitId: string; toUnitId: string; amountMinor: number; date?: string; method?: string }
) {
  return addDoc(collection(db, 'groups', groupId, 'settlements'), {
    fromUnitId: input.fromUnitId,
    toUnitId: input.toUnitId,
    amountMinor: input.amountMinor,
    date: input.date || todayISO(),
    method: input.method || '',
    createdBy: uid,
    createdAt: serverTimestamp()
  });
}

export const deleteSettlement = (groupId: string, settlementId: string) =>
  deleteDoc(doc(db, 'groups', groupId, 'settlements', settlementId));

/* ---------- invites ---------- */

export async function rotateInvite(group: Group): Promise<string> {
  const code = newInviteCode();
  const batch = writeBatch(db);
  batch.set(doc(db, 'invites', code), {
    groupId: group.id, groupName: group.name, createdBy: group.createdBy, createdAt: serverTimestamp()
  });
  batch.update(doc(db, 'groups', group.id), { inviteCode: code });
  if (group.inviteCode) batch.delete(doc(db, 'invites', group.inviteCode));
  await batch.commit();
  return code;
}

export const inviteUrl = (code: string): string =>
  `${publicOrigin()}/join/${code}`;

/**
 * Invite links always use the app's public address, so a link shared from an
 * install on the old .web.app address still sends people to the real one. Dev
 * builds keep the current origin so the join flow can be tried locally.
 */
function publicOrigin(): string {
  const configured: string | undefined = import.meta.env.VITE_PUBLIC_ORIGIN;
  return import.meta.env.PROD && configured ? configured.replace(/\/+$/, '') : window.location.origin;
}

